import { useMemo } from "react"

export type CheckBoxMultiSelectOptions = { [value: string]: string }
export type CheckBoxMultiSelectChildProps = {
  items: {
    value: string
    label: string
    select: (on: boolean) => void
    isSelected: boolean
  }[]
  selectAll: (on: boolean) => void
  isSelectedAll: boolean
}

export type CheckBoxMultiSelectProps = {
  options: CheckBoxMultiSelectOptions
  selections: string[]
  onChange: (values: string[]) => void
  selectAllText?: string
  className?: string
  children?: React.FC<CheckBoxMultiSelectChildProps>
}

export function CheckBoxMultiSelect(props: CheckBoxMultiSelectProps) {
  const { options, selections, onChange, selectAllText, className, children: RenderChildren } = props
  const selectionSet = new Set<string>(selections)

  const items = useMemo(() => {
    return Object.entries(options).map(([value, label]) => ({
      value,
      label,
      select: (on: boolean) => {
        const newSet = new Set(selectionSet)
        if (on) {
          newSet.add(value)
        } else {
          newSet.delete(value)
        }
        onChange([...newSet])
      },
      isSelected: selectionSet.has(value),
    }))
  }, [options, selections])

  const selectAll = (on: boolean) => {
    if (on) {
      const newSelections = new Set(Object.keys(options))
      onChange([...newSelections])
    } else {
      onChange([])
    }
  }
  const isSelectedAll = selectionSet.size == Object.keys(options).length

  return (
    <div className={className}>
      {RenderChildren ? (
        <RenderChildren items={items} selectAll={selectAll} isSelectedAll={isSelectedAll} />
      ) : (
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-2 border-b pb-1.5">
            <input
              className="accent-navy-800 pointer-events-none rounded border-gray-300 hover:accent-blue-500"
              type="checkbox"
              checked={isSelectedAll}
              onChange={() => selectAll(!isSelectedAll)}
            />
            {selectAllText || "All"}
          </label>
          {items.map(({ value, label, select, isSelected }) => {
            return (
              <label key={value} className="flex items-center gap-2">
                <input
                  className="accent-navy-800 pointer-events-none rounded border-gray-300 hover:accent-blue-500"
                  type="checkbox"
                  value={value}
                  checked={isSelected}
                  onChange={(event) => select(event.target.checked)}
                />
                {label}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
